import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { collectionProducts } from '../../mocks/collections';

interface Product {
  id: string;
  name: string;
  price: number;
  originalPrice?: number;
  image: string;
  badge?: string;
  category: string;
}

const collectionTitles: Record<string, { title: string; description: string }> = {
  '/collections/latest': { title: '最新上架', description: '探索我們最新到貨的中國童裝系列' },
  '/collections/girls': { title: '女童服裝', description: '時尚可愛的女童服飾系列' },
  '/collections/girls-sets': { title: '女童套裝', description: '精心搭配的女童套裝組合' },
  '/collections/girls-autumn-winter': { title: '女童秋冬系列', description: '溫暖舒適的秋冬女童服飾' },
  '/collections/girls-spring-summer': { title: '女童春夏系列', description: '清新活潑的春夏女童服飾' },
  '/collections/girls-tops-bottoms': { title: '女童上衣/褲子', description: '百搭實用的女童單品' },
  '/collections/girls-dresses': { title: '女童連衣裙', description: '優雅甜美的女童裙裝' },
  '/collections/girls-outerwear': { title: '女童外套', description: '時尚保暖的女童外套系列' },
  '/collections/girls-suits': { title: '女童西裝', description: '正式優雅的女童西裝' },
  '/collections/girls-leggings': { title: '女童內搭褲', description: '舒適百搭的女童內搭褲' },
  '/collections/boys': { title: '男童服裝', description: '帥氣時尚的男童服飾系列' },
  '/collections/boys-all': { title: '男童全部', description: '所有男童服飾商品' },
  '/collections/boys-autumn-winter': { title: '男童秋冬系列', description: '溫暖舒適的秋冬男童服飾' },
  '/collections/boys-spring-summer': { title: '男童春夏系列', description: '清爽活力的春夏男童服飾' },
  '/collections/boys-tops-bottoms': { title: '男童上衣/褲子', description: '實用百搭的男童單品' },
  '/collections/boys-outerwear': { title: '男童外套', description: '帥氣保暖的男童外套系列' },
  '/collections/boys-suits': { title: '男童西裝', description: '正式帥氣的男童西裝' },
  '/collections/boys-accessories': { title: '男童配飾', description: '時尚實用的男童配件' },
  '/collections/big-kids': { title: '大童系列', description: '適合大童的時尚服飾' },
  '/collections/big-girls': { title: '大童女裝', description: '適合大女童的時尚服飾' },
  '/collections/big-boys': { title: '大童男裝', description: '適合大男童的時尚服飾' },
  '/collections/baby': { title: 'BABY系列', description: '柔軟舒適的嬰兒服飾' },
  '/collections/baby-all': { title: 'BABY全部', description: '所有嬰兒服飾商品' },
  '/collections/baby-boy': { title: 'BABY男童', description: '可愛舒適的男嬰服飾' },
  '/collections/baby-girl': { title: 'BABY女童', description: '甜美柔軟的女嬰服飾' },
  '/collections/homewear': { title: '家居服', description: '舒適溫馨的兒童家居服' },
  '/collections/kids-items': { title: '兒童用品', description: '實用貼心的兒童用品' },
  '/collections/shoes': { title: '童裝鞋', description: '舒適時尚的兒童鞋履' },
};

const CollectionPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [sortBy, setSortBy] = useState('featured');
  const [collectionTitle, setCollectionTitle] = useState('');
  const [collectionDescription, setCollectionDescription] = useState('');

  useEffect(() => {
    const pathKey = location.pathname;
    const collectionInfo = collectionTitles[pathKey] || { title: '商品系列', description: '' };
    setCollectionTitle(collectionInfo.title);
    setCollectionDescription(collectionInfo.description);
    const categoryProducts = collectionProducts[pathKey] || [];
    setProducts(categoryProducts);
    setSortBy('featured');
  }, [location.pathname]);

  const handleSort = (value: string) => {
    setSortBy(value);
    let sortedProducts = [...products];
    switch (value) {
      case 'price-low':
        sortedProducts.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        sortedProducts.sort((a, b) => b.price - a.price);
        break;
      case 'name-az':
        sortedProducts.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        break;
    }
    setProducts(sortedProducts);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <div className="max-w-7xl mx-auto px-4 py-8 mt-32">
        {/* 分類標題 */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">{collectionTitle}</h1>
          {collectionDescription && (
            <p className="text-gray-600 text-sm">{collectionDescription}</p>
          )}
        </div>

        {/* 篩選和排序欄 */}
        <div className="flex justify-between items-center mb-6 pb-4 border-b">
          <div className="text-sm text-gray-600">共 {products.length} 件商品</div>
          <div className="flex items-center gap-2">
            <label htmlFor="sort" className="text-sm text-gray-700">排序：</label>
            <select
              id="sort"
              value={sortBy}
              onChange={(e) => handleSort(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-pink-500"
            >
              <option value="featured">精選推薦</option>
              <option value="price-low">價格：低至高</option>
              <option value="price-high">價格：高至低</option>
              <option value="name-az">名稱：A-Z</option>
            </select>
          </div>
        </div>

        {/* 產品網格 */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-product-shop>
          {products.map((product) => (
            <div
              key={product.id}
              className="group cursor-pointer"
              onClick={() => navigate(`/product/${product.id}`)}
            >
              <div className="relative overflow-hidden rounded-lg mb-3 bg-gray-50">
                <div className="aspect-[3/4] w-full">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                {product.badge && (
                  <div className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded-md font-medium">
                    {product.badge}
                  </div>
                )}
              </div>

              <div className="space-y-1">
                <h3 className="text-sm font-medium text-gray-900 line-clamp-2 group-hover:text-pink-600 transition-colors">
                  {product.name}
                </h3>
                <div className="flex items-center gap-2">
                  <span className="text-base font-bold text-gray-900">HK${product.price}</span>
                  {product.originalPrice && (
                    <span className="text-sm text-gray-400 line-through">HK${product.originalPrice}</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 空狀態 */}
        {products.length === 0 && (
          <div className="text-center py-16">
            <i className="ri-shopping-bag-line text-6xl text-gray-300 mb-4"></i>
            <p className="text-gray-500 text-lg">此分類暫無商品</p>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default CollectionPage;
